import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Subject, combineLatest } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthenticationService, PlatformService } from '@app/services';
import { PermissionService } from '@app/services/permission.service';
import { ExternalAuthConfig, OidcProvider } from '@app/models/authentification-config';
import { Location } from '@angular/common';
import { oidcMessage } from '@app/models/authentification-config'
import { User } from '@app/models/user';

const storageUsernameKey = 'portail-remember-login';


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  @Input() homeCard = false;

  loginForm: FormGroup = this.formBuilder.group({
    username: ['', { validators: [Validators.required] }],
    password: ['', { validators: [Validators.required] }],
    remember: [true]
  });

  loginError: string;
  normalLoginLoading = false;
  showPass: boolean = false;

  redirecting: boolean;
  redirectingMessage: string;

  user: User = null;

  onDestroy$ = new Subject<void>();
  externalAuthConf: ExternalAuthConfig;
  oidcProvider: OidcProvider = null
  oidcMessage: oidcMessage = null
  oidcMustGoLogout: boolean = false;
  oidcAutoConnect: string = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    public authService: AuthenticationService,
    public permService: PermissionService,
    public platformService: PlatformService,
    private location: Location
  ) {
  }

  ngOnInit() {

    this.authService.getExternalAuthConf()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((conf: ExternalAuthConfig) => {
        this.externalAuthConf = conf
        this.updateUser(this.authService.currentUserValue)

        // Set OIDC Provider from user
        this.authService.currentUser$
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((user: User) => {
            this.updateUser(user)
          })


        // Watch for oidc message (after oidc login)
        this.authService.oidcMessage$
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((msg: oidcMessage) => {
            this.oidcMessage = msg
            if (this.oidcMessage?.providerId) {
              // Update OIDC provider from message
              this.oidcProvider = this.externalAuthConf?.providers?.find(p => p.providerId === this.oidcMessage?.providerId)
            }

            if (this.oidcMessage?.type === 'infoUserNotFound') {
              switch (this.oidcProvider?.disconnectProviderAtLogout) {
                case 'no': this.oidcMustGoLogout = false; break;
                case 'yes':
                case 'userChoice':
                  this.oidcMustGoLogout = true; break;
              }
            }

          })
      })


    const inputEmail = localStorage.getItem(storageUsernameKey);

    if (inputEmail) {
      this.loginForm.get('username').setValue(inputEmail);
    }


    if (this.route.snapshot.queryParamMap.has('redirect') && !!this.authService.currentUserValue) { // simple redirect when already logged (for reload purpose)
      this.authService.redirectAfterLogin(this.authService.currentUserValue, this.route.snapshot)

    } else if (this.route.snapshot.queryParamMap.has('oidc-auto-connect')) { // oidc auto connect
      if (!this.authService.currentUserValue) {
        const oidcAutoConnect = this.route.snapshot.queryParamMap.get('oidc-auto-connect')
        this.oidcAutoConnect = oidcAutoConnect
      } else {
        // retirer le paramètre 'oidc-auto-connect' de la barre d'adresse (sans navigation)
        const url = new URL(window.location.href);
        url.searchParams.delete('oidc-auto-connect');
        this.location.replaceState(url.pathname, url.search, window.history.state)
      }

    } else { // Normal login
      this.loginForm.valueChanges.subscribe(_ => {
        this.loginForm.setErrors(null)
        this.loginError = null;
      })
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  updateUser(user: User) {
    this.user = user
    if (this.user?.oidcProviderId) {
      this.oidcProvider = this.externalAuthConf?.providers?.find(p => p.providerId === this.user?.oidcProviderId)
    }
  }

  login() {
    this.loginError = null;
    if (this.loginForm.invalid) {
      return;
    }

    this.normalLoginLoading = true;
    this.authService.login(this.loginForm.value)
      .pipe(finalize(() => {
        this.loginForm.markAsPristine();
        this.normalLoginLoading = false;
      }))
      .subscribe({
        next: user => {

          if (user && user.token) {
            if (this.loginForm.get('remember').value) {
              localStorage.setItem(storageUsernameKey, this.loginForm.value?.username);
            }
            this.authService.redirectAfterLogin(user, this.route.snapshot)
          }

        }, error: error => {
          this.loginError = error;
        }
      });
  }

  clearOidcMessage(redirectToLogin = false) {
    this.oidcMessage = null;
    this.authService.setOidcMessage(null)
    if (redirectToLogin) {
      if (this.oidcMustGoLogout) {
        this.router.navigate(['/logout'])
      } else {
        this.location.go('/login') // modify url without change route
      }
    }
  }

}

